/**
 * Browser Notification Service
 * Handles browser notifications for incoming calls and other events
 */

class NotificationService {
  private hasPermission = false;
  private activeNotification: Notification | null = null;

  constructor() {
    this.checkPermission();
  }

  /**
   * Check if we have notification permission
   */
  private checkPermission() {
    if ('Notification' in window) {
      this.hasPermission = Notification.permission === 'granted';
    }
  }

  /**
   * Request notification permission from user
   */
  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('[Notification] Browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      this.hasPermission = true;
      return true;
    }

    if (Notification.permission !== 'denied') {
      try {
        const permission = await Notification.requestPermission();
        this.hasPermission = permission === 'granted';
        
        if (this.hasPermission) {
          console.log('[Notification] Permission granted');
        } else {
          console.warn('[Notification] Permission denied');
        }
        
        return this.hasPermission;
      } catch (error) {
        console.error('[Notification] Error requesting permission:', error);
        return false;
      }
    }

    return false;
  }

  /**
   * Show incoming call notification
   */
  showIncomingCall(
    callerNumber: string,
    candidateName?: string,
    onClickAction?: () => void
  ): void {
    if (!this.hasPermission) {
      console.warn('[Notification] No permission to show notifications');
      return;
    }

    // Close any existing notification
    if (this.activeNotification) {
      this.activeNotification.close();
    }

    try {
      const title = 'Incoming Call 📞';
      const body = candidateName
        ? `${candidateName} (${callerNumber})`
        : callerNumber;

      this.activeNotification = new Notification(title, {
        body,
        icon: '/phone-icon.png',
        badge: '/phone-icon.png',
        tag: 'incoming-call',
        requireInteraction: true,
        silent: false, // Play sound
      });

      // Handle click - focus window
      this.activeNotification.onclick = () => {
        window.focus();
        
        if (onClickAction) {
          onClickAction();
        }
        
        this.activeNotification?.close();
      };

      // Auto-close after 30 seconds
      setTimeout(() => {
        this.activeNotification?.close();
        this.activeNotification = null;
      }, 30000);

      console.log('[Notification] Showed incoming call notification');
    } catch (error) {
      console.error('[Notification] Error showing notification:', error);
    }
  }

  /**
   * Close active notification
   */
  closeNotification(): void {
    if (this.activeNotification) {
      this.activeNotification.close();
      this.activeNotification = null;
    }
  }

  /**
   * Show generic notification
   */
  show(title: string, body: string, options?: NotificationOptions): void {
    if (!this.hasPermission) {
      return;
    }

    try {
      const notification = new Notification(title, {
        body,
        icon: '/favicon.ico',
        ...options,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);
    } catch (error) {
      console.error('[Notification] Error showing notification:', error);
    }
  }

  /**
   * Check if notifications are supported
   */
  isSupported(): boolean {
    return 'Notification' in window;
  }

  /**
   * Get current permission status
   */
  getPermissionStatus(): NotificationPermission {
    return 'Notification' in window ? Notification.permission : 'denied';
  }
}

// Export singleton instance
export const notificationService = new NotificationService();


